import { useMemo } from "react"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import {
    fetchUsagePerTenantPerResource,
    ServiceCenterStatisticsQuery,
    ServiceCenterUsagePerTenantPerResourceResponse,
} from "../../statisticsApi"
import type { UsagePerTenantPerResourceEntry } from "../../statisticsTypes"

import { ResourceTypeGroupViewModel, toResourceGroups } from "./tenantUsageViewModel"

// The `usagePerTenantPerResource` response carries no `resource.resourceType`, so we cannot
// group by type from a single "all types" call. Instead we request one resource type at a
// time (up to 3) and label each section by the requested type.

type TypeSelection = Readonly<{
    groups: ReadonlyArray<ResourceTypeGroupViewModel>
    rates: ReadonlyArray<UsagePerTenantPerResourceEntry>
}>

// Tag each rate with the resource type it was requested for (the response omits it),
// so the CSV export can label rows by type.
function tagRates(
    data: ServiceCenterUsagePerTenantPerResourceResponse,
    resourceType: ResourceType,
): ReadonlyArray<UsagePerTenantPerResourceEntry> {
    return data.usageRates.map((entry) => ({
        ...entry,
        resource: { ...entry.resource, resourceType },
    }))
}

const selectRoom = (data: ServiceCenterUsagePerTenantPerResourceResponse): TypeSelection => ({
    groups: toResourceGroups(data, ResourceType.ROOM),
    rates: tagRates(data, ResourceType.ROOM),
})

const selectMobileAV = (data: ServiceCenterUsagePerTenantPerResourceResponse): TypeSelection => ({
    groups: toResourceGroups(data, ResourceType.MOBILE_AV),
    rates: tagRates(data, ResourceType.MOBILE_AV),
})

const selectCar = (data: ServiceCenterUsagePerTenantPerResourceResponse): TypeSelection => ({
    groups: toResourceGroups(data, ResourceType.CAR),
    rates: tagRates(data, ResourceType.CAR),
})

type UsagePerTenantResourcesDeps = Readonly<{
    serviceCenterId: number
    startDate: string
    endDate: string
    companyId?: number
}>

type UsagePerTenantResourcesResult = Readonly<{
    groups: ReadonlyArray<ResourceTypeGroupViewModel>
    rates: ReadonlyArray<UsagePerTenantPerResourceEntry>
    isInitialLoading: boolean
}>

export function useUsagePerTenantResources(
    deps: UsagePerTenantResourcesDeps,
    resourceType: ResourceType | undefined,
): UsagePerTenantResourcesResult {
    const roomEnabled = resourceType === undefined || resourceType === ResourceType.ROOM
    const mobileAVEnabled = resourceType === undefined || resourceType === ResourceType.MOBILE_AV
    const carEnabled = resourceType === undefined || resourceType === ResourceType.CAR

    const roomResult = useApiQuery(fetchUsagePerTenantPerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_PER_RESOURCE,
        dependencies: { ...deps, resourceType: ResourceType.ROOM },
        select: selectRoom,
        enabled: roomEnabled,
    })

    const mobileAVResult = useApiQuery(fetchUsagePerTenantPerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_PER_RESOURCE,
        dependencies: { ...deps, resourceType: ResourceType.MOBILE_AV },
        select: selectMobileAV,
        enabled: mobileAVEnabled,
    })

    const carResult = useApiQuery(fetchUsagePerTenantPerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_USAGE_PER_TENANT_PER_RESOURCE,
        dependencies: { ...deps, resourceType: ResourceType.CAR },
        select: selectCar,
        enabled: carEnabled,
    })

    const groups = useMemo<ReadonlyArray<ResourceTypeGroupViewModel>>(() => {
        const result: ResourceTypeGroupViewModel[] = []
        if (roomEnabled && roomResult.data) result.push(...roomResult.data.groups)
        if (mobileAVEnabled && mobileAVResult.data) result.push(...mobileAVResult.data.groups)
        if (carEnabled && carResult.data) result.push(...carResult.data.groups)
        return result
    }, [roomEnabled, mobileAVEnabled, carEnabled, roomResult.data, mobileAVResult.data, carResult.data])

    const rates = useMemo<ReadonlyArray<UsagePerTenantPerResourceEntry>>(() => {
        const result: UsagePerTenantPerResourceEntry[] = []
        if (roomEnabled && roomResult.data) result.push(...roomResult.data.rates)
        if (mobileAVEnabled && mobileAVResult.data) result.push(...mobileAVResult.data.rates)
        if (carEnabled && carResult.data) result.push(...carResult.data.rates)
        return result
    }, [roomEnabled, mobileAVEnabled, carEnabled, roomResult.data, mobileAVResult.data, carResult.data])

    const isInitialLoading =
        (roomEnabled && roomResult.isInitialLoading) ||
        (mobileAVEnabled && mobileAVResult.isInitialLoading) ||
        (carEnabled && carResult.isInitialLoading)

    return { groups, rates, isInitialLoading }
}
